import { type BooleanInput } from '@angular/cdk/coercion';
import { booleanAttribute, computed, Directive, inject, input } from '@angular/core';
import { classes } from '@spartan-ng/helm/utils';
import { cva } from 'class-variance-authority';

import { HlmSidebarService } from './hlm-sidebar.service';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button ring-sidebar-ring active:bg-components-background-primary-lightest active:text-components-content-primary-darker data-[active=true]:bg-components-background-primary-lightest data-[active=true]:text-components-content-primary-darker data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground gap-xs p-sm data-[active=true]:component-medium-semibold flex w-full justify-start overflow-hidden rounded-md text-left transition-[width,height,padding] outline-none group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 hover:cursor-pointer focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 disabled:hover:cursor-default aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>_ng-icon]:size-4 [&>_ng-icon]:shrink-0 group-data-[collapsible=icon]:[&>span]:hidden',
  {
    variants: {
      variant: {
        default: 'hover:text-components-content-neutral-primary hover:bg-components-background-neutral-main',
        outline:
          'bg-background shadow-sidebar-border hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-sidebar-accent',
      },
      size: {
        default: 'component-medium-medium',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

@Directive({
  selector: 'button[hlmSidebarMenuButton], a[hlmSidebarMenuButton]',
  host: {
    'data-slot': 'sidebar-menu-button',
    'data-sidebar': 'menu-button',
    '[attr.data-size]': 'size()',
    '[attr.data-active]': 'isActive()',
  },
})
export class HlmSidebarMenuButton {
  private readonly _sidebarService = inject(HlmSidebarService);

  public readonly variant = input<'default' | 'outline'>('default');
  public readonly size = input<'default' | 'sm' | 'lg'>('default');
  public readonly isActive = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

  protected readonly _isTooltipHidden = computed(
    () => this._sidebarService.state() !== 'collapsed' || this._sidebarService.isMobile(),
  );

  constructor() {
    classes(() => sidebarMenuButtonVariants({ variant: this.variant(), size: this.size() }));
  }
}
